import { useState, useRef, useEffect} from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  Paper,
  Popper,
} from "@mui/material"
import SearchIcon from '@mui/icons-material/Search';
import { alpha, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import useLayout from "@/features/layout/useLayout";
import {
  ackSidebarSearchFocus,
  selectShouldFocusSidebarSearch,
} from "@/features/layout/layoutSlice";

const VerticalMenuSearch = ({ value, onChange, placeholder }) =>  {
  const dispatch = useDispatch();
  const { isSidebarCollapsed } = useLayout();
  const shouldFocusSidebarSearch = useSelector(selectShouldFocusSidebarSearch);
  const theme = useTheme()
  const anchorRef = useRef(null)
  const inputRef = useRef(null)
  const [pop, setPop] = useState(false)
  
  useEffect(() => {
    if (isSidebarCollapsed && pop) {
      setPop(false)
    }
  }, [isSidebarCollapsed, pop])

  useEffect(() => {
    if (!shouldFocusSidebarSearch) return
    inputRef.current?.focus()
    dispatch(ackSidebarSearchFocus())
  }, [shouldFocusSidebarSearch, dispatch])

  return (
    <Box
      sx={{
        px: 1.5,
        pb: 1,
        display: 'flex',
        justifyContent: isSidebarCollapsed ? 'flex-start' : 'center'
      }}
    >
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={() => !isSidebarCollapsed && setPop((v) => !v)}
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: isSidebarCollapsed ? 'divider' : 'transparent',
          '&:hover': { bgcolor: 'action.selected' },
          transition: 'all 1.3s cubic-bezier(0.77, 0, 0.175, 1)'
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>

      {isSidebarCollapsed && (
        <Paper
          elevation={0}
          sx={{
            ml: 1,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.04)
                : 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            px: 1.25,
            py: 0.75,
            transition: 'all 0.25s ease'
          }}
        >
          <InputBase
            inputRef={inputRef}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{ width: '100%', fontSize: '0.9rem' }}
          />
        </Paper>
      )}

      <Popper
        open={!isSidebarCollapsed && pop}
        anchorEl={anchorRef.current}
        placement="right-start"
        sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
        modifiers={[{ name: 'offset', options: { offset: [8, 0] } }]}
      >
        <ClickAwayListener onClickAway={() => setPop(false)}>
          <Paper
            elevation={6}
            sx={{
              width: 260,
              display: 'flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backdropFilter: 'blur(8px) saturate(160%)',
              bgcolor: theme.palette.sidebar.popperBg,
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.25s ease'
            }}
            onMouseLeave={() => setPop(false)}
          >
            <InputBase
              inputRef={inputRef}
              autoFocus
              placeholder={`${placeholder}...`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setPop(false)
                }
              }}
              sx={{ width: '100%', fontSize: '0.9rem' }}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
}

export default VerticalMenuSearch;